/**
 * Cookie Policy — explains what browser storage the Service uses.
 * Edit the table below when you add new tracking cookies or storage keys.
 */
import { Link } from 'react-router-dom';

const rows = [
  { name: 'cookie-consent-v1',       kind: 'Essential',      purpose: 'Records your choice to accept or decline non-essential cookies. Prevents the banner from re-appearing on every page.', where: 'localStorage', ttl: 'Until cleared' },
  { name: 'token / access_token',    kind: 'Essential',      purpose: 'Keeps you signed in. Required for admin, staff, and customer accounts.', where: 'localStorage', ttl: '24 hours (customer) or session (staff)' },
  { name: 'fwtv_sid',                kind: 'Analytics',      purpose: 'Anonymous session ID for public FWTV pages (view / play / search events). No personal data.', where: 'localStorage', ttl: 'Persistent until browser storage cleared' },
  { name: 'fwb_customer_v1',         kind: 'Essential',      purpose: 'Bookshop customer session + basket state.', where: 'localStorage', ttl: '24 hours' },
  { name: 'consent-banner-dismissed:*', kind: 'Essential',   purpose: 'Records that you have acknowledged the current data-processing policy version.', where: 'localStorage', ttl: 'Until the policy version changes' },
];

export default function CookiePolicy() {
  return (
    <div className="max-w-3xl mx-auto p-6 md:p-10 text-slate-800" data-testid="cookie-policy-page">
      <p className="text-xs text-slate-400 mb-2"><Link to="/" className="underline">← Home</Link></p>
      <h1 className="text-3xl font-bold text-slate-900">Cookie Policy</h1>
      <p className="text-xs text-slate-500 mt-1">Last updated: {new Date().toISOString().split('T')[0]}</p>

      <div className="prose prose-slate max-w-none mt-6 text-sm leading-relaxed space-y-4">
        <p>This page lists every browser-side storage key the Service sets. We use <em>localStorage</em> rather than traditional HTTP cookies wherever possible — this keeps your data on your device and out of network requests until you actively use the feature.</p>

        <h2 className="text-lg font-semibold mt-6">What we store</h2>
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-xs border-collapse">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="p-2 border">Name</th>
                <th className="p-2 border">Kind</th>
                <th className="p-2 border">Purpose</th>
                <th className="p-2 border">Where</th>
                <th className="p-2 border">Lifetime</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name} className="align-top">
                  <td className="p-2 border font-mono">{r.name}</td>
                  <td className="p-2 border">
                    <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded ${r.kind === 'Essential' ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'}`}>{r.kind}</span>
                  </td>
                  <td className="p-2 border">{r.purpose}</td>
                  <td className="p-2 border">{r.where}</td>
                  <td className="p-2 border">{r.ttl}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="text-lg font-semibold mt-6">Managing your preferences</h2>
        <p>You can clear any of the entries above at any time from your browser's Site Data / Storage settings. Signing out clears authentication tokens.</p>
        <p>Declining non-essential cookies via the banner disables anonymous analytics (<code>fwtv_sid</code>) but does not affect essential storage needed to sign in, hold your basket, or record legal consent.</p>

        <h2 className="text-lg font-semibold mt-6">Third-party services</h2>
        <p>Payment providers (Stripe / PayPal), when used, set their own cookies during checkout. Their cookie policies apply on their domains.</p>

        <h2 className="text-lg font-semibold mt-6">Contact</h2>
        <p>Questions about this policy? Please write to your organisation's administrator or the contact email published by your organisation.</p>

        <p className="text-xs text-slate-500 mt-8 border-t pt-4">This template is provided for convenience and does not constitute legal advice.</p>
      </div>
    </div>
  );
}
